import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Link,
  Alert,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Snackbar,
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { posStorePromotionService, upcomingPromotionService } from '../services'
import useAuthStore from '../store/authStore'
import { canExportData, isPromotionsOnlyActor } from '../utils/adminPermissions'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import TableEmptyState from '../components/shared/TableEmptyState'
import { fmtAmount } from '../components/SalesReport/formatters'
import PromoReportFilters from '../components/PosStorePromotions/PromoReportFilters'
import PromoReportSummaryCards from '../components/PosStorePromotions/PromoReportSummaryCards'

const COLUMN_COUNT = 14

function formatInvoiceDate(ymd) {
  if (!ymd) return '—'
  const [year, month, day] = String(ymd).split('-').map(Number)
  if (!year || !month || !day) return String(ymd)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(new Blob([blob]))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

function filenameFromDisposition(header, fallback) {
  const match = /filename="([^"]+)"/.exec(header || '')
  return match?.[1] || fallback
}

function PosStorePromotionsReportPage() {
  const navigate = useNavigate()
  const [filterParams, setFilterParams] = useState(null)
  const [resetKey, setResetKey] = useState(0)

  const [summary, setSummary] = useState({})
  const [summaryLoading, setSummaryLoading] = useState(false)

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)

  const [showZeroToGrocers, setShowZeroToGrocers] = useState(false)
  const [settingsSaving, setSettingsSaving] = useState(false)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const showDownloadExcel = canExportData(adminPermissions)
  const canChangeVisibility = !isPromotionsOnlyActor(
    isSuperuserActor,
    adminPermissions,
  )

  useEffect(() => {
    let cancelled = false
    upcomingPromotionService
      .getReimbursementSettings()
      .then((data) => {
        if (!cancelled) {
          setShowZeroToGrocers(data?.show_zero_lines_to_grocers === true)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const handleVisibilityChange = async (event) => {
    const next = event.target.checked
    const previous = showZeroToGrocers
    setShowZeroToGrocers(next)
    try {
      setSettingsSaving(true)
      await upcomingPromotionService.updateReimbursementSettings(next)
    } catch (error) {
      setShowZeroToGrocers(previous)
    } finally {
      setSettingsSaving(false)
    }
  }

  const [exporting, setExporting] = useState(false)
  const [exportNotice, setExportNotice] = useState({
    open: false,
    severity: 'success',
    message: '',
  })

  const handleFiltersChange = useCallback((params) => {
    setFilterParams(params)
    setResetKey((k) => k + 1)
    setPage(0)
  }, [])

  useEffect(() => {
    setPage(0)
  }, [resetKey])

  const stableFilters = useMemo(() => filterParams, [filterParams])

  useEffect(() => {
    if (!stableFilters) return undefined
    let cancelled = false
    const run = async () => {
      try {
        setSummaryLoading(true)
        const data = await posStorePromotionService.getReportSummary(stableFilters)
        if (!cancelled) setSummary(data || {})
      } catch (error) {
        console.error('Error fetching promo report summary:', error)
        if (!cancelled) setSummary({})
      } finally {
        if (!cancelled) setSummaryLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [stableFilters])

  useEffect(() => {
    if (!stableFilters) return undefined

    let cancelled = false
    const run = async () => {
      try {
        setLoading(true)
        const response = await posStorePromotionService.getSalesReport({
          ...stableFilters,
          page: page + 1,
          limit: rowsPerPage,
        })
        if (!cancelled) {
          setItems(Array.isArray(response.items) ? response.items : [])
          setTotal(response.pagination?.total || 0)
        }
      } catch (error) {
        console.error('Error fetching promo reimbursement report:', error)
        if (!cancelled) {
          setItems([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [stableFilters, page, rowsPerPage])

  const handleOpenInvoice = (orderId) => {
    if (!orderId) return
    navigate(`/pos-store-promotions/invoices/${orderId}`, {
      state: { backPath: '/pos-store-promotions/report' },
    })
  }

  const showExportNotice = (severity, message) => {
    setExportNotice({ open: true, severity, message })
  }

  const handleExportExcel = async () => {
    if (exporting || !stableFilters || total === 0) return
    try {
      setExporting(true)
      const response = await posStorePromotionService.downloadReimbursementReport(
        stableFilters,
      )
      const fallback = `promo-reimbursement_${stableFilters.startDate}_${stableFilters.endDate}.xlsx`
      const filename = filenameFromDisposition(
        response.headers?.['content-disposition'],
        fallback,
      )
      downloadBlob(response.data, filename)
      showExportNotice('success', 'Excel download is ready.')
    } catch (error) {
      console.error('Error exporting promo reimbursement report:', error)
      showExportNotice('error', 'Could not export the report. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const exportDisabled = exporting || loading || !stableFilters || total === 0

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
        Reports
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Promo reimbursement by shop, invoice, and article
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Promotion-scoped report only
        </Typography>
        <Typography variant="body2">
          This report lists sale and return lines that have an applied promotion,
          including lines reimbursed at 0. Return rows use the original sale
          reimbursement. Quantity and Total Reimbursement Amount are the positive
          amounts reversed by the return. For full store sales, use the standard
          Sales Reports.
        </Typography>
      </Alert>

      {canChangeVisibility && (
        <FormControlLabel
          sx={{ mb: 2, display: 'flex' }}
          control={
            <Switch
              checked={showZeroToGrocers}
              onChange={handleVisibilityChange}
              disabled={settingsSaving}
            />
          }
          label="Show 0 reimbursement lines to grocers"
        />
      )}

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent>
          <PromoReportFilters onFiltersChange={handleFiltersChange} />

          <PromoReportSummaryCards summary={summary} loading={summaryLoading} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#666' }}>
              Total records: {total}
            </Typography>
            {showDownloadExcel ? (
              <Button
                variant="outlined"
                size="small"
                disabled={exportDisabled}
                onClick={handleExportExcel}
                startIcon={
                  exporting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  borderColor: '#e90000',
                  color: '#e90000',
                  '&:hover': { borderColor: '#c40000', bgcolor: 'rgba(233,0,0,0.04)' },
                }}
              >
                {exporting ? 'Exporting…' : 'Download Excel'}
              </Button>
            ) : null}
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Shop ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Shop</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Article Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Article Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Quantity
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    MRP
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Discounted Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Unit Discounted Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Unit Reimburse Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Total Reimbursement Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={COLUMN_COUNT} />
                ) : items.length === 0 ? (
                  <TableEmptyState colSpan={COLUMN_COUNT} message="No promo invoices found" />
                ) : (
                  items.map((row, index) => (
                    <TableRow
                      key={`${row.invoiceType || 'Sale'}-${row.invoiceNumber}-${row.sku}-${index}`}
                      hover
                    >
                      <TableCell>{row.orgId ?? '—'}</TableCell>
                      <TableCell>{row.orgName}</TableCell>
                      <TableCell>{row.invoiceType || 'Sale'}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleOpenInvoice(row.invoiceNumber)}
                          sx={{ fontWeight: 600, textDecoration: 'none', color: '#e90000' }}
                        >
                          {row.invoiceNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{formatInvoiceDate(row.invoiceDate)}</TableCell>
                      <TableCell>{row.sku}</TableCell>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell align="right">
                        {Number(row.quantity || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">{fmtAmount(row.mrp)}</TableCell>
                      <TableCell align="right">{fmtAmount(row.discountedPrice)}</TableCell>
                      <TableCell align="right">
                        {fmtAmount(row.unitDiscountedAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtAmount(row.unitReimburseAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtAmount(
                          row.invoiceType === 'Return'
                            ? Math.abs(Number(row.totalReimbursementAmount) || 0)
                            : row.totalReimbursementAmount,
                        )}
                      </TableCell>
                      <TableCell>{row.reason || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>

      <Snackbar
        open={exportNotice.open}
        autoHideDuration={4000}
        onClose={() => setExportNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={exportNotice.severity}
          onClose={() => setExportNotice((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {exportNotice.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default PosStorePromotionsReportPage
